import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import {
  type FreightClass,
  HandlingUnitTypeLabels,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import type { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import type { FreightQuote } from "@redotech/redo-fulfillment-operations-common/freight/freight-quote-request";
import { convertWeight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { AddressCard } from "./address-card";
import type { FreightBookingState } from "./freight-booking-types";
import { QuoteCard } from "./quote-card";

interface BookStepProps {
  state: FreightBookingState;
  selectedQuote: FreightQuote;
  originAddress: FulfillmentOrderAddress;
  destinationAddress: FulfillmentOrderAddress;
}

export const BookStep = memo(function BookStep({
  state,
  selectedQuote,
  originAddress,
  destinationAddress,
}: BookStepProps) {
  const freightSummary = useMemo(() => {
    const totalUnits = state.freight.handlingUnits.reduce(
      (sum, u) => sum + u.quantity,
      0,
    );
    const unitTypes = [
      ...new Set(
        state.freight.handlingUnits.map((u) =>
          HandlingUnitTypeLabels[u.type].toLowerCase(),
        ),
      ),
    ];
    const unitLabel =
      totalUnits === 1
        ? (unitTypes[0] ?? "unit")
        : `${totalUnits} ${unitTypes.length === 1 ? `${unitTypes[0]}s` : "units"}`;

    const totalWeightLbs = state.freight.products.reduce((sum, p) => {
      return (
        sum + convertWeight(p.itemWeight, WeightUnit.Lb).value * p.quantity
      );
    }, 0);

    const freightClasses = [
      ...new Set(
        state.freight.handlingUnits
          .map((u) => u.freightClass)
          .filter((c): c is FreightClass => c !== undefined),
      ),
    ];
    const classText =
      freightClasses.length > 0
        ? `Class ${freightClasses.slice(0, -1).join(", ")}${freightClasses.length > 1 ? `, & ${freightClasses[freightClasses.length - 1]}` : freightClasses[0]}`
        : undefined;

    return [
      unitLabel,
      `${Math.round(totalWeightLbs).toLocaleString()} lbs`,
      classText,
    ]
      .filter(Boolean)
      .join(" • ");
  }, [state.freight]);

  return (
    <Flex
      dir="column"
      gap="xl"
      mx="auto"
      p="3xl"
      style={{ maxWidth: 880 }}
      w="full"
    >
      {/* Route card */}
      <Flex
        bgColor="primary"
        borderColor="primary"
        borderWidth="1px"
        dir="column"
        gap="xl"
        p="2xl"
        radius="sm"
      >
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Route
        </Text>
        <Flex gap="3xl">
          <Flex dir="column" grow="1">
            <AddressCard address={originAddress} title="Pickup location" />
            {state.route.pickupDateTime && (
              <Flex dir="column" gap="none">
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  Pickup time:{" "}
                  {state.route.pickupDateTime.toLocaleString("en-US", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex dir="column" grow="1">
            <AddressCard
              address={destinationAddress}
              title="Delivery location"
            />
          </Flex>
        </Flex>
      </Flex>

      {/* Freight card */}
      <Flex
        bgColor="primary"
        borderColor="primary"
        borderWidth="1px"
        dir="column"
        gap="xl"
        p="2xl"
        radius="sm"
      >
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Freight
        </Text>
        <Text fontSize="xs" textColor="primary">
          {freightSummary}
        </Text>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Items
          </Text>
          {state.freight.products.map((product) => (
            <Text fontSize="xs" key={product.id} textColor="primary">
              {product.name}
              {product.sku ? ` - ${product.sku}` : ""}{" "}
              <Text as="span" fontSize="xs" fontWeight="semibold">
                ({product.quantity} units)
              </Text>
            </Text>
          ))}
        </Flex>
      </Flex>

      {/* Carrier card */}
      <QuoteCard
        alwaysShowDetails
        onSelect={() => {}}
        quote={selectedQuote}
        selectable={false}
        selected={false}
      />
    </Flex>
  );
});
